/**
 * RouteRobotsManager — single source of truth for the <meta name="robots"> tag.
 *
 * GSC requires that "Page Not Found" / Soft-404 pages emit `noindex`. Our SPA
 * has a dynamic `/:slug` route that, on an unknown slug, renders the canonical
 * `<NotFound>` component — but historically the static index.html shipped a
 * default `<meta name="robots" content="index, follow…">` that beat (or
 * coexisted with) the Helmet-injected `noindex`, leaving fake URLs flagged as
 * Soft-404s in Google Search Console.
 *
 * This component lives at the root of the router. On every navigation:
 *   1. Match the current pathname against a whitelist of valid route patterns
 *      (everything wired in <App.js> + <EsRouter.jsx>).
 *   2. If matched → set robots = "index, follow, max-image-preview:large,
 *      max-snippet:-1, max-video-preview:-1".
 *   3. If NOT matched → set robots = "noindex, nofollow".
 *
 * Page-level `<Helmet>` tags (e.g. <NotFound>'s noindex) still work — Helmet's
 * deduplication takes the deepest meta. This manager is defense-in-depth for
 * pages that don't ship their own robots tag.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// ---- Whitelist of indexable route patterns (regex-ready) -------------------
// Order doesn't matter; first match wins. Keep in sync with <App.js> Routes.
const VALID_PATTERNS = [
  /^\/$/,
  /^\/about-us\/?$/,
  /^\/our-team\/?$/,
  /^\/our-offices\/?$/,
  /^\/contact-us\/?$/,
  /^\/testimonials\/?$/,
  /^\/form-finances\/?$/,
  /^\/financing\/?$/,
  /^\/specials\/?$/,
  /^\/areas-we-serve\/?$/,
  /^\/sitemap\/?$/,
  /^\/terms\/?$/,
  /^\/privacy\/?$/,
  /^\/hipaa-npp\/?$/,
  /^\/fortworthdental\/?$/,
  /^\/press-releases(\/.+)?\/?$/,
  /^\/blogs(\/.+)?\/?$/,
  /^\/blog(\/.+)?\/?$/,
  /^\/implant-center(\/.+)?\/?$/,
  /^\/top-dentists-fort-worth\/?$/,
  /^\/dental-implants-fort-worth\/?$/,
  /^\/best-dentist-(fort-worth|near-me)\/?$/,
  /^\/dental-implant-cost-fort-worth\/?$/,
  /^\/invisalign-cost-fort-worth\/?$/,
  // Spanish — full sub-tree (EsSEO inside is the source of truth for ES pages)
  /^\/es(\/.*)?$/,
  // Admin (still indexable=false but logged-in only — handled separately by
  // <AdminLayout>; treat as "do not flag as soft-404")
  /^\/admin(\/.*)?$/,
  // ---------------------------------------------------------------------
  // Permissive single-segment catch — covers ALL 583 dynamic location-page
  // slugs (e.g. /dental-implants-arlington, /general-dentistry-fort-worth,
  // /dentist-near-cleburne) PLUS the 26 service pages and 5 special
  // sub-pages. <NotFound> still imperatively overrides to "noindex" with a
  // data-fwd-locked tag for any slug that doesn't resolve to real content,
  // so unknown URLs are correctly de-indexed.
  // ---------------------------------------------------------------------
  /^\/[a-z][a-z0-9-]+\/?$/,
];

// /:slug is dynamic and resolves to either ServiceAreaPage or <NotFound>.
// We MUST NOT default-allow it — instead, ServiceAreaPage's own <Helmet> sets
// `index, follow` for valid slugs, and <NotFound>'s sets `noindex, follow`.
// So we leave single-segment /:slug uncovered here and trust the page-level
// Helmet to override our fallback `noindex, nofollow`.
const isWhitelistedRoute = (pathname) => VALID_PATTERNS.some((re) => re.test(pathname));

const ROBOTS_INDEX = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
const ROBOTS_NOINDEX = 'noindex, nofollow';

const upsertRobots = (content) => {
  if (typeof document === 'undefined') return;
  // Remove every robots tag we might have left around so we don't end up with
  // both `index, follow` and `noindex` simultaneously (Google takes the most
  // permissive when there are conflicting tags from the same origin).
  document.head.querySelectorAll('meta[name="robots"]').forEach((el) => {
    if (!el.dataset.fwdLocked) el.remove();
  });
  const meta = document.createElement('meta');
  meta.setAttribute('name', 'robots');
  meta.setAttribute('content', content);
  meta.dataset.fwdRouteRobots = '1';
  document.head.appendChild(meta);
};

const RouteRobotsManager = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const allow = isWhitelistedRoute(pathname);
    upsertRobots(allow ? ROBOTS_INDEX : ROBOTS_NOINDEX);
  }, [pathname]);

  return null;
};

export default RouteRobotsManager;
